import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { ArrowLeftIcon, PencilIcon } from '@heroicons/react/24/outline';

const TripGpsDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { get, loading } = useApi();
  const [record, setRecord] = useState(null);
  const [trip, setTrip] = useState(null);
  const [event, setEvent] = useState(null);
  const [video, setVideo] = useState(null);

  useEffect(() => {
    fetchRecord();
  }, [id]);

  const fetchRecord = async () => {
    const { data } = await get(`/api/trip-gps/${id}`);
    if (data) {
      setRecord(data);
      
      // Fetch related data
      if (data.tripId) {
        const tripRes = await get(`/api/trips/${data.tripId}`);
        if (tripRes.data) setTrip(tripRes.data);
      }
      if (data.eventId) {
        const eventRes = await get(`/api/events/${data.eventId}`);
        if (eventRes.data) setEvent(eventRes.data);
      }
      if (data.videoId) {
        const videoRes = await get(`/api/videos/${data.videoId}`);
        if (videoRes.data) setVideo(videoRes.data);
      }
    }
  };

  if (loading) return <LoadingSpinner />;
  if (!record) return <div className="p-6">GPS Record not found</div>;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/trip-gps/filter')}
            className="mr-4 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
          <h1 className="text-2xl font-bold text-gray-800">GPS Record Details</h1>
        </div>
        <button
          onClick={() => navigate(`/trip-gps/edit/${id}`)}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center space-x-2"
        >
          <PencilIcon className="h-5 w-5" />
          <span>Edit</span>
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-sm text-gray-500">ID</label>
            <p className="text-lg font-medium">{record.id}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Trip</label>
            <p className="text-lg font-medium">
              {trip ? (
                <button
                  onClick={() => navigate(`/trips/${trip.id}`)}
                  className="text-indigo-600 hover:text-indigo-900"
                >
                  Trip #{trip.id} - {trip.label || 'No label'}
                </button>
              ) : (
                `Trip #${record.tripId}`
              )}
            </p>
          </div>
          {record.eventId && (
            <div>
              <label className="text-sm text-gray-500">Event</label>
              <p className="text-lg font-medium">
                {event ? (
                  <button
                    onClick={() => navigate(`/events/${event.id}`)}
                    className="text-indigo-600 hover:text-indigo-900"
                  >
                    Event #{event.id} - {event.eventType?.description || 'Unknown'}
                  </button>
                ) : (
                  `Event #${record.eventId}`
                )}
              </p>
            </div>
          )}
          {record.videoId && (
            <div>
              <label className="text-sm text-gray-500">Video</label>
              <p className="text-lg font-medium">
                {video ? (
                  <button
                    onClick={() => navigate(`/videos/${video.id}`)}
                    className="text-indigo-600 hover:text-indigo-900"
                  >
                    Video #{video.id}
                  </button>
                ) : (
                  `Video #${record.videoId}`
                )}
              </p>
            </div>
          )}
          <div>
            <label className="text-sm text-gray-500">Timestamp</label>
            <p className="text-lg font-medium">
              {record.timestamp ? new Date(record.timestamp).toLocaleString() : '-'}
            </p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Coordinates</label>
            <p className="text-lg font-medium">
              {record.latitude && record.longitude ? (
                <a
                  href={`https://www.google.com/maps?q=${record.latitude},${record.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:text-indigo-900"
                >
                  {record.latitude.toFixed(6)}, {record.longitude.toFixed(6)}
                </a>
              ) : '-'}
            </p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Altitude</label>
            <p className="text-lg font-medium">
              {record.altitude ? `${record.altitude.toFixed(1)} m` : '-'}
            </p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Speed</label>
            <p className="text-lg font-medium">
              {record.speed ? `${record.speed.toFixed(1)} km/h` : '-'}
            </p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Heading</label>
            <p className="text-lg font-medium">
              {record.heading ? `${record.heading.toFixed(1)}°` : '-'}
            </p>
          </div>
        </div>

        {/* Map Link */}
        {record.latitude && record.longitude && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-2">View on Map</h3>
            <iframe
              title="Google Maps"
              width="100%"
              height="300"
              frameBorder="0"
              style={{ border: 0, borderRadius: '0.5rem' }}
              src={`https://www.google.com/maps?q=${record.latitude},${record.longitude}&hl=es;z=14&output=embed`}
              allowFullScreen
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default TripGpsDetails;